import { useEffect, useRef, useState } from 'react';
import {
  minQueryLengthFor,
  searchCodes,
  type Code,
  type CodeSearchStatus,
  type CodeType,
} from '../services/codesService';

/**
 * Keystroke settle time before a request is issued.
 *
 * 400 ms rather than 250: there is no cache at any layer, so every settled keystroke is a round
 * trip to Postgres. A code is typically 5 characters, and at 250 ms an ordinary typist's pauses
 * were enough to fire two or three requests on the way to one useful one. This is still well
 * inside the ~500 ms at which a dropdown starts to feel sluggish.
 */
const DEBOUNCE_MS = 400;

export interface UseCodeSearchResult {
  /** Suggestions for the current query. Empty while loading, or when nothing matched. */
  codes: Code[];
  /** True while a request is in flight — drives the spinner and the aria-live announcement. */
  loading: boolean;
  /**
   * Whether the set can serve suggestions at all.
   *
   * Deliberately separate from {@link loading}, and deliberately not named `LOADING`:
   * `status === 'NOT_READY'` means "this set cannot help you" (not synced yet, or switched off),
   * while `loading === true` means "a request is in flight right now". They are different
   * questions and both are needed at once, which is exactly why the status value avoids the word.
   */
  status: CodeSearchStatus;
  error: boolean;
}

/**
 * Debounced code lookup for the coding inputs (feature 046).
 *
 * Owns the async plumbing once — debounce, in-flight state, and discarding superseded responses —
 * so `CodeAutocomplete` and `ChipInput` do not each grow their own copy of it.
 *
 * @param type Which coding set to search, or `undefined` to disable lookups entirely (the input
 *   then behaves exactly as it did before this feature).
 * @param query The current input text.
 */
export function useCodeSearch(type: CodeType | undefined, query: string): UseCodeSearchResult {
  const [codes, setCodes] = useState<Code[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<CodeSearchStatus>('NOT_READY');
  const [error, setError] = useState(false);

  // The query whose response we are still willing to accept. A response for anything else is
  // stale by definition and must be dropped — without this, a slow request for "27" resolving
  // after a fast one for "274" would repaint the dropdown with options for text the biller has
  // already replaced.
  const latestQuery = useRef(query);

  useEffect(() => {
    const trimmed = query.trim();
    latestQuery.current = trimmed;

    if (!type || trimmed.length < minQueryLengthFor(type)) {
      setCodes([]);
      setLoading(false);
      setError(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(false);

    const timer = setTimeout(() => {
      searchCodes(type, trimmed, { signal: controller.signal })
        .then((result) => {
          // Belt and braces alongside the abort: aborting is not synchronous, so a response can
          // still arrive after the input moved on.
          if (latestQuery.current !== trimmed) return;
          setStatus(result.status);
          setCodes(result.codes);
          setLoading(false);
        })
        .catch(() => {
          if (latestQuery.current !== trimmed) return;
          // An abort is the normal path when the biller keeps typing, and must not surface as an
          // error — the request that replaced it will report the real outcome.
          if (controller.signal.aborted) return;
          setError(true);
          setCodes([]);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [type, query]);

  return { codes, loading, status, error };
}
